import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GetAccountMailingAddressCountryDocument } from "#graphql/supergraphHooks";
import {
  type GetAccountMailingAddressCountryQuery,
  type GetAccountMailingAddressCountryQueryVariables,
} from "#graphql/supergraphTypes";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import {
  evaluateMailingCountryMismatch,
  type MailingCountry,
  type MailingCountryEvaluation,
} from "./utils";

type MismatchState = MailingCountryEvaluation & { loading: boolean };

const IDLE_STATE: MismatchState = {
  isMismatch: false,
  loading: false,
  commonMailingCountryObjectId: undefined,
  commonMailingCountryObjectName: undefined,
};

/**
 * Detects whether the accounts' mailing countries conflict with the selected bank country.
 *
 * Two data sources, chosen up-front:
 *  - LP portal flow: `accounts` in context already carry `mailingAddress` (pre-fetched via
 *    unifiedPortalAccounts) and the check runs synchronously.
 *  - Position/account flow: only one account is in play, so GetAccountMailingAddressCountry
 *    is fired for `currentPosition.accountId`.
 *
 * Either way the resolved per-account countries are fed into a single `evaluateMailingCountryMismatch`
 * call so the mismatch and common-country derivation lives in one place.
 */
export const useIsMailingCountryMismatch = (): MismatchState => {
  const {
    selectedBankCountry,
    accounts: contextAccounts,
    currentPosition,
  } = useConsolidatedPaymentPrefContext();
  const client = useApolloClient();
  const [state, setState] = useState<MismatchState>(IDLE_STATE);

  const contextCountryName = selectedBankCountry?.name;

  const hasContextMailingData =
    !!contextAccounts &&
    contextAccounts.length > 0 &&
    contextAccounts.some((edge) => edge.node.mailingAddress !== undefined);

  // Non-LP flows only ever resolve a single account (the position's account).
  const queryAccountId = !hasContextMailingData ? currentPosition?.accountId : undefined;

  useEffect(() => {
    if (hasContextMailingData && contextAccounts) {
      const countries: MailingCountry[] = contextAccounts.map(
        (edge) => edge.node.mailingAddress?.country ?? null,
      );
      setState({
        ...evaluateMailingCountryMismatch(countries, contextCountryName),
        loading: false,
      });
      return;
    }

    if (!queryAccountId) {
      setState(IDLE_STATE);
      return;
    }

    let cancelled = false;
    setState({ ...IDLE_STATE, loading: true });

    void client
      .query<
        GetAccountMailingAddressCountryQuery,
        GetAccountMailingAddressCountryQueryVariables
      >({
        query: GetAccountMailingAddressCountryDocument,
        variables: { accountId: queryAccountId },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const country: MailingCountry = data?.account?.mailingAddress?.country ?? null;
        setState({
          ...evaluateMailingCountryMismatch([country], contextCountryName),
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) setState(IDLE_STATE);
      });

    return () => {
      cancelled = true;
    };
    // hasContextMailingData/queryAccountId already cover contextAccounts/currentPosition transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContextMailingData, queryAccountId, contextCountryName]);

  return state;
};
